import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getDashboardPath } from "../../../utils/authRedirect";
import { isClientRole } from "../../../utils/roles";
import { usePortal } from "../../../context/PortalContext";
import { PORTALS } from "../../../utils/portalTypes";
import AuthRegisterByLinkFlow from "../Auth/AuthRegisterByLinkFlow";
import RegistrationByLinkSuccess from "../Auth/RegistrationByLinkSuccess";
import {
  RegistrationLinkError,
  RegistrationLinkLoading,
} from "../Auth/RegistrationLinkState";
import authAPI from "../../../services/authApi";
import a from "../Auth/auth.module.css";
import Seo from "../../Seo";

const portal = PORTALS.patient;

export default function PatientRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get("link");
  const { subdomain, clinic } = usePortal();
  const { isAuth, role } = useSelector((state) => state.auth);
  const [linkInfo, setLinkInfo] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [linkLoading, setLinkLoading] = useState(Boolean(linkToken));
  const [byLinkPending, setByLinkPending] = useState(false);
  const [byLinkSubmittedEmail, setByLinkSubmittedEmail] = useState("");
  const [byLinkSubmitting, setByLinkSubmitting] = useState(false);
  const [byLinkErr, setByLinkErr] = useState(null);

  useEffect(() => {
    if (!linkToken) {
      navigate(portal.login, { replace: true });
    }
  }, [linkToken, navigate]);

  useEffect(() => {
    if (isAuth && isClientRole(role)) {
      navigate(getDashboardPath(role), { replace: true });
    }
  }, [isAuth, role, navigate]);

  useEffect(() => {
    if (!linkToken) return;
    setLinkLoading(true);
    authAPI
      .getRegistrationLinkInfo(linkToken)
      .then((r) => {
        setLinkInfo(r.link);
        setLinkError(null);
      })
      .catch((e) => {
        setLinkError(
          e.response?.data?.error || "Ссылка недействительна или истекла",
        );
      })
      .finally(() => setLinkLoading(false));
  }, [linkToken]);

  const submitByLink = async (data) => {
    setByLinkErr(null);
    setByLinkSubmitting(true);
    try {
      await authAPI.registerByLink({
        token: linkToken,
        subdomain,
        registrationType: data.registrationType || "family",
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        children: data.children,
        dateOfBirth: data.dateOfBirth,
      });
      setByLinkSubmittedEmail(String(data.email || "").trim());
      setByLinkPending(true);
    } catch (e) {
      setByLinkErr(
        e.response?.data?.error || e.message || "Не удалось отправить заявку",
      );
    } finally {
      setByLinkSubmitting(false);
    }
  };

  if (!linkToken) {
    return null;
  }

  if (byLinkPending) {
    return (
      <>
        <Seo noindex title="Заявка отправлена" />
        <RegistrationByLinkSuccess
          clubName={clinic?.name}
          email={byLinkSubmittedEmail}
          loginPath={portal.login}
        />
      </>
    );
  }

  if (linkLoading) {
    return (
      <>
        <Seo noindex title="Заявка в клуб" />
        <RegistrationLinkLoading loginPath={portal.login} />
      </>
    );
  }

  if (linkError) {
    return (
      <>
        <Seo noindex title="Ссылка недоступна" />
        <RegistrationLinkError message={linkError} loginPath={portal.login} />
      </>
    );
  }

  return (
    <>
      <Seo noindex title="Заявка в клуб" />
      <AuthRegisterByLinkFlow
        clubName={clinic?.name}
        linkInfo={linkInfo}
        portalId="patient"
        backTo={portal.login}
        backLabel="Назад"
        loading={byLinkSubmitting}
        error={byLinkErr}
        onSubmit={submitByLink}
        footer={
          <div className={a.footerLinksModern}>
            <Link to={portal.login}>Уже есть аккаунт? Войти</Link>
          </div>
        }
      />
    </>
  );
}
